import React from "react";
import { HiChevronRight, HiTrash, HiPencilSquare } from "react-icons/hi2";
import { AthleteProgram, formatAssigner } from "../../services/programService";
import { ProgramProgressBar, ProgramStateChip } from "./ProgramProgress";
import { formatProgramDateRange, formatWeeks } from "./programFormat";

interface ProgramCardProps {
  program: AthleteProgram;
  onOpen: () => void;
  /** Self programs only: opens the builder. */
  onEdit?: () => void;
  /** Self programs only: cascade delete. */
  onDelete?: () => void;
  deleting?: boolean;
}

/**
 * List card for the Programs page (plan 3g): name, who assigned it, weeks,
 * total duration, progress bar and the current week.
 */
const ProgramCard: React.FC<ProgramCardProps> = ({
  program,
  onOpen,
  onEdit,
  onDelete,
  deleting = false,
}) => {
  const { progress } = program;
  return (
    <div className="rounded-xl border border-base-300 bg-base-100 shadow-sm hover:shadow-md transition-shadow">
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left px-5 py-4 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-base-content font-grotesk truncate max-w-full">
                {program.name}
              </h3>
              <ProgramStateChip
                progress={progress}
                durationWeeks={program.durationWeeks}
              />
              {program.fromLinkageOnly && (
                <span className="inline-flex items-center rounded-full bg-base-300/70 px-2 py-0.5 text-[10px] font-medium text-base-content/60 font-grotesk">
                  Removed by coach
                </span>
              )}
            </div>
            <p className="mt-0.5 text-sm text-base-content/60 font-grotesk truncate">
              Assigned by {formatAssigner(program.assigner)}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-base-content/60 font-grotesk">
              <span className="font-geist tabular-nums">
                {formatWeeks(program.durationWeeks)}
              </span>
              <span className="text-base-content/30">·</span>
              <span className="font-geist tabular-nums">
                {formatProgramDateRange(progress.startDate, progress.endDate)}
              </span>
            </div>
          </div>
          <HiChevronRight className="text-xl text-base-content/30 flex-shrink-0 mt-1" />
        </div>
        <ProgramProgressBar progress={progress} className="mt-3" />
      </button>

      {(onEdit || onDelete) && (
        <div className="border-t border-base-300 px-3 py-1.5 flex justify-end gap-1">
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="btn btn-ghost btn-sm min-h-11 h-11 rounded-xl text-xs font-medium font-grotesk normal-case text-base-content/70"
              aria-label={`Edit ${program.name}`}
            >
              <HiPencilSquare className="text-sm" />
              Edit
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="btn btn-ghost btn-sm min-h-11 h-11 rounded-xl text-xs font-medium font-grotesk normal-case text-error hover:bg-error/10 disabled:opacity-50"
              aria-label={`Delete ${program.name}`}
            >
              <HiTrash className="text-sm" />
              {deleting ? "Deleting..." : "Delete"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgramCard;
